/** Driver commands preserve cloud/draft/cached routes and captured request scope. */
import { useCallback } from 'react';
import { offlineRunPlan } from '@/utils/offlineRunPlan';
import {
  assignCachedRunProposalUser, assignOfflineDraftRunUser, assignRunUser, isOfflineDraftRun,
  notifyRunDriver, unassignCachedRunProposalUser, unassignOfflineDraftRunUser, unassignRunUser,
} from '@/api/runs';

const useDriverAssignment = ({ selectedRunId, selectedRun, commands, offline = false,
  runCacheLastUpdated = null }) => {
  const source = offline ? isOfflineDraftRun(selectedRun) ? 'draft' : 'cached' : 'cloud';
  const handleAssignUser = useCallback(async userId => {
    if (!selectedRunId || !userId) return false;
    const result = await commands.execute({ kind: 'assign', runId: selectedRunId, source, expectedDriver: userId,
      ...(offline ? { runPlan: offlineRunPlan(selectedRun) } : {}) }, {
      send: config => source === 'draft' ? assignOfflineDraftRunUser(selectedRunId, userId, config)
        : source === 'cached' ? assignCachedRunProposalUser(selectedRunId, userId, runCacheLastUpdated, config)
          : assignRunUser(selectedRunId, userId, config),
      message: source === 'cloud' ? 'Driver assigned' : 'Driver proposed for this offline run',
    });
    return result.accepted;
  }, [commands, selectedRunId, selectedRun, source, runCacheLastUpdated, offline]);

  const handleUnassignUser = useCallback(async () => {
    if (!selectedRunId) return false;
    const result = await commands.execute({ kind: 'unassign', runId: selectedRunId, source,
      ...(offline ? { runPlan: offlineRunPlan(selectedRun) } : {}) }, {
      send: config => source === 'draft' ? unassignOfflineDraftRunUser(selectedRunId, config)
        : source === 'cached' ? unassignCachedRunProposalUser(selectedRunId, runCacheLastUpdated, config)
          : unassignRunUser(selectedRunId, config),
      message: source === 'cloud' ? 'Driver unassigned' : 'Offline driver proposal removed',
    });
    return result.accepted;
  }, [commands, selectedRunId, selectedRun, source, runCacheLastUpdated, offline]);

  const assignUserToRun = useCallback(async (runId, userId) => {
    if (offline || !runId || !userId) return false;
    const result = await commands.execute({ kind: 'assign', runId, source: 'cloud', expectedDriver: userId }, {
      send: config => assignRunUser(runId, userId, config), message: 'Driver assigned',
    });
    return result.accepted;
  }, [commands, offline]);

  const unassignUserFromRun = useCallback(async runId => {
    if (offline || !runId) return false;
    const result = await commands.execute({ kind: 'unassign', runId, source: 'cloud' }, {
      send: config => unassignRunUser(runId, config), message: 'Driver unassigned',
    });
    return result.accepted;
  }, [commands, offline]);

  const handleNotifyDriver = useCallback(async runId => {
    const targetId = runId || selectedRunId;
    if (offline || !targetId) return false;
    const result = await commands.execute({ kind: 'notify', runId: targetId, source: 'cloud' }, {
      send: config => notifyRunDriver(targetId, config), message: 'Driver notification request accepted', refresh: false,
    });
    return result.accepted;
  }, [commands, offline, selectedRunId]);

  return { handleAssignUser, handleUnassignUser, assignUserToRun, unassignUserFromRun, handleNotifyDriver };
};

export default useDriverAssignment;
